import {
  useCallback,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { cn } from '@docuid/ui/lib/utils';

interface MagicCardProperties {
  children?: ReactNode;
  className?: string;
  gradientSize?: number;
  gradientColor?: string;
  gradientOpacity?: number;
  gradientFrom?: string;
  gradientTo?: string;
  cardBg?: string;
}

export function MagicCard({
  children,
  className,
  gradientSize = 200,
  gradientColor = 'var(--accent)',
  gradientOpacity = 0.12,
  gradientFrom = 'var(--accent)',
  gradientTo = 'var(--border)',
  cardBg,
}: MagicCardProperties) {
  const [position, setPosition] = useState({ x: -gradientSize, y: -gradientSize });
  const [hovered, setHovered] = useState(false);

  const gradientBorder = `linear-gradient(var(--card) 0 0) padding-box, radial-gradient(${String(gradientSize)}px circle at ${String(position.x)}px ${String(position.y)}px, ${gradientFrom}, ${gradientTo} 100%) border-box`;

  const spotlight = `radial-gradient(${String(gradientSize)}px circle at ${String(position.x)}px ${String(position.y)}px, ${gradientColor}, transparent 100%)`;

  const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  }, []);

  const handlePointerLeave = useCallback(() => {
    setHovered(false);
    setPosition({ x: -gradientSize, y: -gradientSize });
  }, [gradientSize]);

  return (
    <div
      className={cn(
        'group relative isolate overflow-hidden rounded-[inherit] border border-transparent',
        className,
      )}
      onPointerMove={handlePointerMove}
      onPointerEnter={() => {
        setHovered(true);
      }}
      onPointerLeave={handlePointerLeave}
      style={{ background: gradientBorder }}
    >
      <div
        className={cn('absolute inset-px z-20 rounded-[inherit]', !cardBg && 'bg-[var(--card)]')}
        style={cardBg ? { background: cardBg } : undefined}
        aria-hidden
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-px z-30 rounded-[inherit] transition-opacity duration-300"
        style={{
          background: spotlight,
          opacity: hovered ? gradientOpacity : 0,
        }}
      />
      <div className="relative z-40">{children}</div>
    </div>
  );
}
